/**
 * The state of a CheckUp calculation, as every reader of it sees it. (F26)
 *
 * One vocabulary for the job statuses a calculation moves through, so the
 * citizen's screens and the company's drawer answer "is a calculation running
 * for this talent?" the same way — the drawer used to know only about the
 * re-runs it had started itself (a client-side flag), and showed a finished
 * old score while a calculation the citizen started was still running.
 *
 * @module heroes/checkup
 */

export const CHECKUP_CALCULATION_JOB_STATUSES = [
  'pending',
  'queued',
  'processing',
  'completed',
  'failed',
] as const;

export type CheckUpCalculationJobStatus = (typeof CHECKUP_CALCULATION_JOB_STATUSES)[number];

export function isCheckUpCalculationJobStatus(
  value: unknown
): value is CheckUpCalculationJobStatus {
  return (
    typeof value === 'string' &&
    (CHECKUP_CALCULATION_JOB_STATUSES as readonly string[]).includes(value)
  );
}

/** A job that has not reached a terminal status. */
export function isCheckUpCalculationRunning(status: CheckUpCalculationJobStatus): boolean {
  return status === 'pending' || status === 'queued' || status === 'processing';
}

/**
 * The calculation a journey's CheckUp is on: the job its assessment names.
 * `startedAt` is the enqueue instant (ISO 8601).
 */
export interface CheckUpCalculationState {
  status: CheckUpCalculationJobStatus;
  jobId: string;
  startedAt: string;
}
